import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useUserStore } from './user'

interface WishlistProduct {
  id: number
  name: string
  slug: string
  price: number
  sale_price?: number
  featured_image: string
  brand_name?: string
  category_name?: string
  in_stock: boolean
  description?: string
  short_description?: string
  sku?: string
  stock_quantity?: number
}

interface WishlistPost {
  id: number
  title: string
  slug: string
  excerpt: string
  featured_image: string
  category_name?: string
  author_name?: string
  created_at: string
  views?: number
  type: 'post'
}

interface WishlistTour {
  id: number
  title: string
  slug: string
  short_description: string
  featured_image: string
  price: number
  sale_price?: number
  duration_days?: number
  category_name?: string
  destination_name?: string
  type: 'tour'
}

type WishlistItem = (WishlistProduct & { type?: 'product' }) | WishlistPost | WishlistTour

export const useWishlistStore = defineStore('wishlist', () => {
  // State
  const wishlistItems = ref<WishlistItem[]>([])

  // Getters
  const wishlistCount = computed(() => wishlistItems.value.length)
  
  const wishlistProducts = computed(() => 
    wishlistItems.value.filter(item => !item.type || item.type === 'product') as WishlistProduct[]
  )
  
  const wishlistPosts = computed(() => 
    wishlistItems.value.filter(item => item.type === 'post') as WishlistPost[]
  )
  
  const wishlistTours = computed(() => 
    wishlistItems.value.filter(item => item.type === 'tour') as WishlistTour[]
  )
  
  // For backward compatibility
  const wishlist = computed(() => wishlistProducts.value)
  
  // Reactive function for checking if product is in wishlist
  const isInWishlist = (productId: number) => {
    return wishlistItems.value.some(item => item.id === productId && (!item.type || item.type === 'product'))
  }

  // Function for checking if post is in wishlist
  const isPostInWishlist = (postId: number) => {
    return wishlistItems.value.some(item => item.id === postId && item.type === 'post')
  }

  // Function for checking if tour is in wishlist
  const isTourInWishlist = (tourId: number) => {
    return wishlistItems.value.some(item => item.id === tourId && item.type === 'tour')
  }

  // Actions
  const addToWishlist = (product: any) => {
    
    // Always allow adding to wishlist, whether authenticated or not
    if (!isInWishlist(product.id)) {
      const wishlistProduct: WishlistProduct = {
        id: product.id,
        name: product.name,
        slug: product.slug,
        price: product.price,
        sale_price: product.sale_price,
        featured_image: product.featured_image,
        brand_name: product.brand_name,
        category_name: product.category_name,
        in_stock: product.in_stock,
        description: product.description,
        short_description: product.short_description,
        sku: product.sku,
        stock_quantity: product.stock_quantity
      }
      
      wishlistItems.value.push(wishlistProduct)
      saveToLocalStorage()
      return true
    } else {
    }
    return true
  }

  const addPostToWishlist = (post: any) => {
    
    if (!isPostInWishlist(post.id)) {
      const wishlistPost: WishlistPost = {
        id: post.id,
        title: post.title,
        slug: post.slug,
        excerpt: post.excerpt,
        featured_image: post.featured_image,
        category_name: post.category_name,
        author_name: post.author_name,
        created_at: post.created_at,
        views: post.views,
        type: 'post'
      }
      
      wishlistItems.value.push(wishlistPost)
      saveToLocalStorage()
      return true
    } else {
    }
    return true
  }

  const addTourToWishlist = (tour: any) => {
    
    if (!isTourInWishlist(tour.id)) {
      const wishlistTour: WishlistTour = {
        id: tour.id,
        title: tour.title,
        slug: tour.slug,
        short_description: tour.short_description,
        featured_image: tour.featured_image,
        price: tour.price,
        sale_price: tour.sale_price,
        duration_days: tour.duration_days,
        category_name: tour.category_name,
        destination_name: tour.destination_name,
        type: 'tour'
      }
      
      wishlistItems.value.push(wishlistTour)
      saveToLocalStorage()
      return true
    } else {
    }
    return true
  }

  const removeFromWishlist = (εκδρομήId: number) => {
    // Always allow removing from wishlist, whether authenticated or not
    const index = wishlistItems.value.findIndex(item => item.id === εκδρομήId)
    if (index > -1) {
      wishlistItems.value.splice(index, 1)
      saveToLocalStorage()
      return true
    }
    return false
  }

  const removePostFromWishlist = (postId: number) => {
    const index = wishlistItems.value.findIndex(item => item.id === postId && item.type === 'post')
    if (index > -1) {
      wishlistItems.value.splice(index, 1)
      saveToLocalStorage()
      return true
    }
    return false
  }

  const removeTourFromWishlist = (tourId: number) => {
    const index = wishlistItems.value.findIndex(item => item.id === tourId && item.type === 'tour')
    if (index > -1) {
      wishlistItems.value.splice(index, 1)
      saveToLocalStorage()
      return true
    }
    return false
  }

  const clearWishlist = () => {
    wishlistItems.value = []
    saveToLocalStorage()
  }

  const saveToLocalStorage = () => {
    try {
      const dataToSave = JSON.stringify(wishlistItems.value)
      localStorage.setItem('pism-wishlist', dataToSave)
      try {
        // Notify other windows/components that wishlist was updated (useful when multiple tabs open)
        window.dispatchEvent(new CustomEvent('wishlist-updated'))
      } catch (e) {
        // noop
      }
    } catch (error) {
    }
  }

  const loadFromLocalStorage = () => {
    try {
      const stored = localStorage.getItem('pism-wishlist')
      if (stored) {
        wishlistItems.value = JSON.parse(stored)
      } else {
      }
    } catch (error) {
      wishlistItems.value = []
    }
  }

  // Initialize from localStorage
  loadFromLocalStorage()

  return {
    wishlistItems,
    wishlist,
    wishlistProducts,
    wishlistPosts,
    wishlistTours,
    wishlistCount,
    isInWishlist,
    isPostInWishlist,
    isTourInWishlist,
    addToWishlist,
    addPostToWishlist,
    addTourToWishlist,
    removeFromWishlist,
    removePostFromWishlist,
    removeTourFromWishlist,
    clearWishlist,
    loadFromLocalStorage
  }
})
